import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, PenTool, Layers, Award, Star, MessageCircle, BookOpen, Zap } from "lucide-react";
import { api, fmt, trackClicSoumission } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { lp } from "../lib/localize";
import { FadeUp, MaskedLine, HoverLift } from "../components/motion";
import RichText from "../components/RichText";
import { SEO } from "../components/SEO";
import { useShopSettings, isSectionOff } from "../lib/shopSettings";
import { PromoBanner } from "../components/PromoBanner";
import { ReturningVisitor } from "../components/ReturningVisitor";
import SuggestionsRecherches from "../components/SuggestionsRecherches";
import { ShareButtons } from "../components/ShareButtons";
import Ribbon from "../components/Ribbon";
import ForfaitBanner from "../components/ForfaitBanner";
import SmsBanner from "../components/SmsBanner";
import { CallRequestDialog } from "../components/CallRequestDialog";
import { EImg, ELink } from "../components/Editable";

const HERO_IMG = "https://images.unsplash.com/photo-1642104744809-14b986179927?q=80&w=1600&auto=format&fit=crop";

const CHAPTER_IMGS = [
  "https://images.unsplash.com/photo-1663657876166-03b3aabb5483?q=80&w=1000&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1732257297170-09599f1bbaec?q=80&w=1000&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1705925716592-259267037a03?q=80&w=1000&auto=format&fit=crop",
];

const FEATURE_ICONS = [PenTool, Layers, Award];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [googleReviews, setGoogleReviews] = useState(null);
  const [temoignages, setTemoignages] = useState([]);  // témoignages clients publiés
  const { lang, t, ts, site } = useLang();
  const shopSettings = useShopSettings();
  const off = (key) => isSectionOff(shopSettings, key);  // sections masquées dans l'admin
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 140]);
  const heroOpacity = useTransform(scrollY, [0, 600], [1, 0.25]);

  useEffect(() => {
    api.get("/products/home-featured", { params: { limit: 8 } })
      .then((res) => setFeatured(res.data.slice(0, 8)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/testimonials", { params: { lang } })
      .then((res) => {
        // Avis Google en direct → cartes + badge ; sinon, si la NOTE RÉELLE est en
        // cache (quota Places épuisé, demande du propriétaire 2026-10-29), le badge
        // montre quand même les vrais chiffres au-dessus des témoignages statiques
        if (res.data?.source === "google" && res.data.reviews?.length) setGoogleReviews(res.data);
        else if (res.data?.rating != null) setGoogleReviews({ ...res.data, reviews: [] });
        else setGoogleReviews(null);
      })
      .catch(() => {});
    // Témoignages clients publiés (rabais −5 %, validés par le propriétaire)
    api.get("/temoignages").then((res) => setTemoignages(res.data?.items || [])).catch(() => {});
  }, [lang]);

  const chapters = t("home.chapters").map((c, i) => ({ ...c, img: CHAPTER_IMGS[i] }));
  const features = t("home.features").map((f, i) => ({ ...f, icon: FEATURE_ICONS[i] }));

  return (
    <div data-testid="home-page">
      <SEO title={ts("seo.home_title")} description={ts("seo.home_desc")} path="/" image="/partage-sociaux.jpg" />
      {/* Bandeaux du haut : l'espace du menu fixe (pt-20) suit le 1er bandeau
          visible — promo OU tournois — même si l'un des deux est masqué */}
      <div className="[&>*:first-child]:pt-20">
        <PromoBanner />
        {!off("bandeau_forfaits") && <ForfaitBanner />}
        <SmsBanner />
      </div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden spotlight">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0">
          <EImg editPath="img.hero_bg" src={HERO_IMG} alt="Trophée sous les projecteurs" className="w-full h-full object-cover opacity-45" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/70" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-24 pb-32">
          {/* Logo ovale : à droite du titre sur grand écran, AU-DESSUS en format compact (mobile) */}
          <div className="lg:flex lg:items-center lg:gap-10 xl:gap-14">
            {/* LCP de la page : ce logo est LA chose à charger en premier —
              fetchpriority high + dimensions explicites (audit SEO 2026-10-28) */}
          <motion.img
              src={site("img.hero_logo") || (lang === "en" ? "/logo-ovale-en.png" : "/logo-ovale.png")} alt="Mr Trophée — trophée doré et emblème"
              data-edit-image="img.hero_logo"
              data-testid="hero-logo"
              fetchpriority="high" decoding="async" width="690" height="434"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="w-full h-auto mb-8 lg:order-2 lg:w-[22rem] xl:w-[26rem] lg:mb-0 lg:shrink-0"
            />
            <div className="lg:order-1 lg:flex-1 lg:min-w-0">
          <motion.p
            key="kicker"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="text-gold tracking-[0.35em] uppercase text-xs mb-8"
          >
            {t("home.hero_kicker")}
          </motion.p>
          <h1 className="font-serif leading-[0.95] text-5xl sm:text-6xl lg:text-[4.6rem] xl:text-[6rem] tracking-tight">
            <MaskedLine key={`l1-${ts("home.hero_l1")}`} delay={0.35}>{t("home.hero_l1")}</MaskedLine>
            <MaskedLine key={`l2-${ts("home.hero_l2")}`} delay={0.55} className="italic text-gold">{t("home.hero_l2")}</MaskedLine>
            <MaskedLine key={`l3-${ts("home.hero_l3")}`} delay={0.75}>{t("home.hero_l3")}</MaskedLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9"
          >
            {/* Bouton « Votre soumission en 30 secondes » — bien visible, sous le titre (demande client) */}
            <ELink editPath="link.home_quote" to="/soumission-rapide" data-testid="hero-cta-quote"
              onClick={() => trackClicSoumission("accueil")}
              className="group relative inline-flex items-center gap-3 bg-gold text-ink font-bold text-base md:text-lg px-10 py-5 rounded-full shadow-xl shadow-gold/25 hover:bg-champagne hover:-translate-y-0.5 transition-all duration-300">
              <span className="absolute inset-0 rounded-full bg-gold/40 animate-ping [animation-duration:2.8s]" aria-hidden="true" />
              <Zap className="w-5 h-5 relative transition-transform duration-300 group-hover:scale-110" />
              <span className="relative">{t("quote.fab_long")}</span>
              <ArrowRight className="w-5 h-5 relative transition-transform duration-300 group-hover:translate-x-1" />
            </ELink>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-ivory/70 text-base md:text-lg leading-relaxed"
          >
            {t("home.hero_sub")}
          </motion.p>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <ELink
              editPath="link.hero_boutique"
              to="/boutique" data-testid="hero-cta-catalogue"
              className="group inline-flex items-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300"
            >
              {t("home.cta_collection")}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </ELink>
            <ELink
              editPath="link.hero_craft"
              to="/#savoir-faire" data-testid="hero-cta-craft"
              className="inline-flex items-center gap-3 border border-ivory/25 text-ivory px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
            >
              {t("home.cta_craft")}
            </ELink>
            <ELink
              editPath="link.hero_catalogues"
              to="/catalogues" data-testid="hero-cta-catalogues"
              className="group inline-flex items-center gap-3 border border-ivory/25 text-ivory px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <BookOpen className="w-5 h-5" />
              {t("home.cta_catalogues")}
            </ELink>
            <ELink
              editPath="link.messenger"
              to="https://m.me/trophee.ca" target="_blank" rel="noreferrer" data-testid="hero-cta-messenger"
              className="inline-flex items-center gap-3 border border-gold/40 text-gold px-8 py-4 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              {t("contact.messenger")}
            </ELink>
            <CallRequestDialog testid="hero-call"
              className="inline-flex items-center gap-3 border border-gold/40 text-gold px-8 py-4 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 0.9 }}
            className="mt-8"
          >
            <ShareButtons title={`Mr Trophée — ${ts("home.hero_l1")} ${ts("home.hero_l2")} ${ts("home.hero_l3")}`} label={t("footer.share_page")} />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-ivory/65"
        >
          <ArrowDown className="w-5 h-5 animate-bounce" />
        </motion.div>
      </section>

      {/* Encart « Bon retour » (demande du propriétaire) : visiteurs récurrents
          seulement, quand une nouveauté est parue depuis leur dernière visite */}
      <ReturningVisitor />

      {/* « Reprendre où vous étiez » — les suggestions 3×3 issues des témoins du
          visiteur prennent la place de l'ancienne section « médailles pour
          animaux » (déplacée dans la boutique seulement — demande du propriétaire,
          2026-10-16). Invisible à la première visite */}
      <section className="border-y border-gold/10 bg-surface/40 py-12 md:py-16" data-testid="home-reco-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SuggestionsRecherches lieu="accueil" />
        </div>
      </section>

      {/* BANDEAU AVIS GOOGLE sous le héros (demande client) : rassurer d'emblée les
          nouveaux visiteurs — note et nombre d'avis EN DIRECT de la fiche Google */}
      <section data-testid="home-google-banner" className="border-y border-gold/10 bg-surface/40">
        <FadeUp className="max-w-7xl mx-auto px-6 lg:px-10 py-5">
          <a
            href={site("link.google") || "https://maps.app.goo.gl/GGpe1K5iDpwHYj2b6"} target="_blank" rel="noreferrer"
            data-testid="google-banner-link"
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 group"
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden="true">
              <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
              <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
            </svg>
            <span className="flex gap-0.5" aria-hidden="true">
              {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.round(googleReviews?.rating ?? 5) ? "fill-gold text-gold" : "text-gold/40"}`} />)}
            </span>
            <span className="text-sm text-champagne font-semibold">
              {googleReviews
                ? `${(googleReviews.rating ?? 5).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { minimumFractionDigits: 1 })}/5 · ${googleReviews.user_rating_count ?? 0} ${ts("home_testimonials.google_reviews")}`
                : t("home_reviews_banner.fallback")}
            </span>
            <span className="text-xs text-ivory/50 underline underline-offset-2 group-hover:text-gold transition-colors duration-300">
              {t("home_reviews_banner.see")}
            </span>
          </a>
        </FadeUp>
      </section>

      <Ribbon />

      {/* MANIFESTO */}
      {!off("home_chapters") && (
      <section id="savoir-faire" data-testid="home-chapters" className="max-w-7xl mx-auto px-6 lg:px-10 py-28 md:py-40 space-y-32">
        {chapters.map((c, i) => (
          <div key={c.num} className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
            <FadeUp className={`md:col-span-5 ${i % 2 ? "md:order-2 md:col-start-8" : ""}`}>
              <span className="font-serif text-7xl md:text-8xl text-outline block leading-none">{c.num}</span>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 mb-6"><RichText text={c.title} path={`home.chapters.${i}.title`} /></h2>
              <p className="text-ivory/60 leading-relaxed text-base md:text-lg max-w-md"><RichText text={c.text} path={`home.chapters.${i}.text`} /></p>
            </FadeUp>
            <FadeUp delay={0.15} className={`md:col-span-7 ${i % 2 ? "md:order-1 md:col-start-1" : ""}`}>
              <HoverLift>
                <div className="card-clip overflow-hidden">
                  <motion.img
                    src={site(`img.chapter_${i}`) || c.img} alt={c.title}
                    data-edit-image={`img.chapter_${i}`}
                    loading="lazy" decoding="async"
                    className="w-full h-[320px] md:h-[440px] object-cover"
                    whileHover={{ scale: 1.05 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
              </HoverLift>
            </FadeUp>
          </div>
        ))}
      </section>
      )}

      {/* L'ATELIER — texte riche pour le référencement (demande du propriétaire,
          2026-10-28, suite à l'audit « contenu mince ») : qui on est, depuis
          quand, ce qu'on fait, pour qui, où — rédigé en ses mots et MODIFIABLE
          par lui en mode « page Word » (chemins home.atelier_*) */}
      {!off("home_atelier") && (
      <section data-testid="home-atelier" className="border-y border-gold/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 md:py-32 text-center">
          <FadeUp>
            <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("home.atelier_kicker")}</p>
            <h2 className="font-serif text-3xl md:text-5xl mb-10 leading-tight">
              <RichText text={t("home.atelier_title_1")} path="home.atelier_title_1" />{" "}
              <span className="italic text-gold"><RichText text={t("home.atelier_title_2")} path="home.atelier_title_2" /></span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="space-y-6 text-ivory/70 leading-relaxed text-base md:text-lg text-left md:text-center">
              <p><RichText text={t("home.atelier_p1")} path="home.atelier_p1" /></p>
              <p><RichText text={t("home.atelier_p2")} path="home.atelier_p2" /></p>
              <p className="text-ivory"><RichText text={t("home.atelier_p3")} path="home.atelier_p3" /></p>
            </div>
          </FadeUp>
        </div>
      </section>
      )}

      {/* COLLECTION */}
      {!off("home_collection") && (
      <section data-testid="home-collection" className="max-w-7xl mx-auto px-6 lg:px-10 pb-28 md:pb-40">
        <FadeUp className="flex items-end justify-between mb-14 flex-wrap gap-6">
          <div>
            <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("home.collection_kicker")}</p>
            <h2 className="font-serif text-4xl md:text-6xl">{t("home.collection_title_1")} <span className="italic text-gold">{t("home.collection_title_2")}</span></h2>
          </div>
          <ELink editPath="link.see_all" to="/boutique" data-testid="collection-see-all" className="group inline-flex items-center gap-2 text-sm text-ivory/70 hover:text-gold transition-colors duration-300">
            {t("home.see_all")}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </ELink>
        </FadeUp>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
          {featured.map((p, i) => {
            const gala = (p.images || []).find((im) => im.includes("lifestyle"));
            return (
            <FadeUp key={p.id} delay={i * 0.1}>
              <Link to={`/produit/${p.id}`} data-testid={`featured-product-${p.id}`} className="group block">
                <div className="card-clip overflow-hidden bg-surface">
                  <motion.img
                    src={gala || p.image} alt={p.name}
                    loading="lazy" decoding="async"
                    className={`w-full h-72 ${gala ? "object-cover" : "object-contain bg-[#f4f2ec]"} opacity-90 group-hover:opacity-100 transition-opacity duration-500`}
                    whileHover={{ scale: 1.06 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
                <div className="pt-5 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs tracking-widest uppercase text-gold/70 mb-1">{lp(p, lang).category}</p>
                    <h3 className="font-serif text-2xl group-hover:text-gold transition-colors duration-300">{lp(p, lang).name}</h3>
                  </div>
                  <p className="text-sm text-ivory/60 whitespace-nowrap pt-1">{t("home.from")} {fmt(p.base_price)}</p>
                </div>
              </Link>
            </FadeUp>
            );
          })}
        </div>
      </section>
      )}

      {/* PERSONNALISATION */}
      {!off("home_craft") && (
      <section data-testid="home-craft" className="border-y border-gold/10 bg-surface/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-28 md:py-36 grid md:grid-cols-2 gap-14 items-center">
          <FadeUp>
            <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("home.craft_kicker")}</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-8">{t("home.craft_title_1")}<br /><span className="italic text-gold">{t("home.craft_title_2")}</span></h2>
            <ul className="space-y-6">
              {features.map((f, i) => (
                <li key={f.title} className="flex gap-5">
                  <f.icon className="w-6 h-6 text-gold shrink-0 mt-1" />
                  <div>
                    <h3 className="font-serif text-xl mb-1"><RichText text={f.title} path={`home.features.${i}.title`} /></h3>
                    <p className="text-ivory/60 text-sm leading-relaxed"><RichText text={f.text} path={`home.features.${i}.text`} /></p>
                  </div>
                </li>
              ))}
            </ul>
            <ELink
              editPath="link.craft"
              to="/boutique" data-testid="craft-cta"
              className="group mt-10 inline-flex items-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300"
            >
              {t("home.craft_cta")}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </ELink>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="relative card-clip overflow-hidden">
              <EImg
                editPath="img.craft"
                src="https://images.unsplash.com/photo-1550438655-400744b9fefc?q=80&w=1200&auto=format&fit=crop"
                alt="Plaque gravée" className="w-full h-[420px] object-cover"
                loading="lazy" decoding="async"
              />
              <div className="absolute bottom-[14%] left-1/2 -translate-x-1/2 w-[55%] px-4 py-2 plate-gravure rounded-sm">
                <p className="font-serif text-center text-sm md:text-base tracking-wide">{t("home.craft_plate")}</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
      )}

      {/* TÉMOIGNAGES */}
      {!off("home_testimonials") && (
      <section data-testid="home-testimonials" className="max-w-7xl mx-auto px-6 lg:px-10 py-28 md:py-36">
        <FadeUp className="mb-14">
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("home_testimonials.kicker")}</p>
          <h2 className="font-serif text-4xl md:text-6xl">{t("home_testimonials.title_1")} <span className="italic text-gold">{t("home_testimonials.title_2")}</span></h2>
          <a
            href={site("link.google") || "https://maps.app.goo.gl/GGpe1K5iDpwHYj2b6"} target="_blank" rel="noreferrer"
            data-edit-link="link.google"
            data-testid="google-rating"
            className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full border border-gold/30 text-sm text-champagne hover:border-gold transition-colors duration-300"
          >
            {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.round(googleReviews?.rating ?? 5) ? "fill-gold text-gold" : "text-gold/40"}`} />)}
            {googleReviews
              ? `${(googleReviews.rating ?? 5).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { minimumFractionDigits: 1 })} — ${googleReviews.user_rating_count ?? 0} ${ts("home_testimonials.google_reviews")}`
              : t("home_testimonials.rating")}
          </a>
        </FadeUp>
        {googleReviews?.reviews?.length ? (
          <div data-testid="testimonials-google">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {googleReviews.reviews.map((r, i) => (
                <FadeUp key={i} delay={i * 0.12}>
                  <figure className="bg-surface border border-gold/15 rounded-xl p-7 h-full flex flex-col hover:border-gold/40 transition-colors duration-500" data-testid={`google-review-${i}`}>
                    <span className="font-serif text-5xl text-gold/30 leading-none mb-4">"</span>
                    <blockquote className="text-ivory/70 text-sm leading-relaxed flex-1 line-clamp-6">{r.text}</blockquote>
                    <figcaption className="mt-6 pt-5 border-t border-ivory/10 flex items-center gap-3">
                      {r.author_photo ? (
                        <img src={r.author_photo} alt={r.author || "Avis Google"} className="w-9 h-9 rounded-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                      ) : (
                        <span className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center font-serif text-gold">{r.author?.[0] || "?"}</span>
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-champagne truncate">{r.author}</p>
                        <div className="flex gap-0.5 my-1">
                          {[...Array(5)].map((_, s) => <Star key={s} className={`w-3.5 h-3.5 ${s < Math.round(r.rating) ? "fill-gold text-gold" : "text-gold/30"}`} />)}
                        </div>
                        <p className="text-xs text-ivory/70">
                          {r.publish_time && new Date(r.publish_time).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { timeZone: "America/Montreal", year: "numeric", month: "long" })}
                          {" · "}{t("home_testimonials.google_badge")}
                        </p>
                      </div>
                    </figcaption>
                    {r.reply && (
                      <div className="mt-5 pt-4 border-t border-gold/25 bg-gold/5 -mx-7 -mb-7 px-7 pb-6 rounded-b-xl" data-testid={`google-reply-${i}`}>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-gold mb-1.5">{t("home_testimonials.owner_reply")}</p>
                        <p className="text-ivory/60 text-sm leading-relaxed">{r.reply}</p>
                      </div>
                    )}
                  </figure>
                </FadeUp>
              ))}
            </div>
            <a
              href={site("link.google") || "https://maps.app.goo.gl/GGpe1K5iDpwHYj2b6"} target="_blank" rel="noreferrer"
              data-edit-link="link.google"
              data-testid="google-see-all"
              className="inline-flex items-center gap-2 mt-8 text-sm text-gold hover:text-champagne transition-colors duration-300"
            >
              {t("home_testimonials.see_all_google")} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t("home_testimonials.items").map((item, i) => (
            <FadeUp key={i} delay={i * 0.12}>
              <figure className="bg-surface border border-gold/15 rounded-xl p-7 h-full flex flex-col hover:border-gold/40 transition-colors duration-500" data-testid={`testimonial-${i}`}>
                <span className="font-serif text-5xl text-gold/30 leading-none mb-4">"</span>
                <blockquote className="text-ivory/70 text-sm leading-relaxed flex-1"><RichText text={item.quote} /></blockquote>
                <figcaption className="mt-6 pt-5 border-t border-ivory/10">
                  <p className="font-semibold text-champagne"><RichText text={item.author} /></p>
                  <div className="flex gap-0.5 my-1.5">
                    {[...Array(5)].map((_, s) => <Star key={s} className="w-3.5 h-3.5 fill-gold text-gold" />)}
                  </div>
                  <p className="text-xs text-ivory/70">{item.org}</p>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>
        )}
        {/* Témoignages clients publiés (rabais −5 %, validés par le propriétaire) */}
        {temoignages.length > 0 && (
          <div className="mt-12" data-testid="testimonials-site">
            <p className="text-xs uppercase tracking-[0.2em] text-ivory/50 mb-5">
              {lang === "en" ? "From our customers" : "De nos clients"}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {temoignages.map((tm, i) => (
                <FadeUp key={i} delay={i * 0.12}>
                  <figure className="bg-surface border border-gold/15 rounded-xl p-7 h-full flex flex-col hover:border-gold/40 transition-colors duration-500" data-testid={`temoignage-site-${i}`}>
                    <span className="font-serif text-5xl text-gold/30 leading-none mb-4">"</span>
                    <blockquote className="text-ivory/70 text-sm leading-relaxed flex-1">{tm.texte}</blockquote>
                    <figcaption className="mt-6 pt-5 border-t border-ivory/10 flex items-center gap-3">
                      <span className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center font-serif text-gold">{tm.nom?.[0] || "?"}</span>
                      <div className="min-w-0">
                        <p className="font-semibold text-champagne truncate">{tm.nom}</p>
                        <p className="text-xs text-ivory/70">{tm.date}</p>
                      </div>
                    </figcaption>
                  </figure>
                </FadeUp>
              ))}
            </div>
          </div>
        )}
      </section>
      )}

      {/* CTA FINAL */}
      {!off("home_final") && (
      <section data-testid="home-final" className="relative py-32 md:py-44 spotlight overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <FadeUp>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight">
              {t("home.final_title_1")}<br /><span className="italic text-gold">{t("home.final_title_2")}</span>
            </h2>
            <p className="text-ivory/60 mt-6 mb-10 max-w-lg mx-auto">{t("home.final_sub")}</p>
            <ELink
              editPath="link.final_boutique"
              to="/boutique" data-testid="final-cta"
              className="group inline-flex items-center gap-3 bg-gold text-ink font-semibold px-10 py-5 rounded-full text-lg hover:bg-champagne transition-colors duration-300"
            >
              {t("home.final_cta")}
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </ELink>
            <a
              href={site("link.messenger") || "https://m.me/trophee.ca"} target="_blank" rel="noreferrer" data-edit-link="link.messenger" data-testid="final-cta-messenger"
              className="group inline-flex items-center gap-3 border border-gold/40 text-gold px-10 py-5 rounded-full text-lg hover:bg-gold hover:text-ink transition-colors duration-300 ml-0 sm:ml-4 mt-4 sm:mt-0"
            >
              <MessageCircle className="w-5 h-5" />
              {t("contact.messenger")}
            </a>
            <CallRequestDialog testid="final-call"
              className="inline-flex items-center gap-3 border border-gold/40 text-gold px-10 py-5 rounded-full text-lg hover:bg-gold hover:text-ink transition-colors duration-300 ml-0 sm:ml-4 mt-4 sm:mt-0" />
          </FadeUp>
        </div>
      </section>
      )}
    </div>
  );
}
